import { kbIds } from './kbIds';
import { relevance } from './relevance';
import { KbId } from '@/types';
import { jsonrepair } from 'jsonrepair';

async function triggerKbsCreation(
  studioId: string,
  params: { obj: string; region: string; spo: string; ta: string },
) {
  return relevance.trigger(studioId, params);
}

async function createKbs({
  obj,
  region,
  spo,
  ta,
  specificStudioId,
}: {
  obj: string;
  region: string;
  spo: string;
  ta: string;
  specificStudioId?: string;
}) {
  let studioIds: KbId[];
  const allStudioIds = await kbIds.get();
  if (specificStudioId) {
    studioIds = allStudioIds.filter((studioId: KbId) => studioId.kbId === specificStudioId);
    if (!studioIds || studioIds.length === 0) {
      throw new Error(`Studio ID ${specificStudioId} not found`);
    }
  } else {
    studioIds = allStudioIds;
    if (!studioIds || studioIds.length === 0) {
      throw new Error('No studio IDs found');
    }
  }

  const params = { obj, region, spo, ta };

  const studiosData = await Promise.all(
    studioIds.map((studioId: KbId) => triggerKbsCreation(studioId.kbId, params)),
  );
  const fullData = studiosData.map((data) => {
    const matchingSection = studioIds.find(
      (studioId: { kbId: string }) => studioId.kbId === data.studio_id,
    );

    return {
      job_id: data.job_id,
      project: data.project,
      studio_id: data.studio_id,
      sec_id: matchingSection ? matchingSection.id : null,
    };
  });

  return fullData;
}

async function getKb({
  jobId,
  studioId,
  sec_id,
}: {
  jobId: string;
  // Kept for call-site compatibility; the server supplies the project now.
  project?: string;
  studioId: string;
  sec_id: string;
}) {
  const data = await relevance.poll(studioId, jobId);
  let answer: string = '';
  const finalData = data.updates.find((update: any) => update.type === 'chain-success');
  if (finalData) {
    if (!finalData?.output?.output?.answer) {
      throw new Error('Invalid KB API response: missing output.output.answer');
    }
    
    answer = finalData.output.output.answer
      .replaceAll('”', '"')
      .replaceAll('“', '"')
      .replaceAll('```json', '')
      .replaceAll('\n```', '')
      .replace(/\\\r?\n/g, '\\n')
      .replace(/\r?\n(?!["\[\]{}\s])/g, '\\n')
      .replace(/([^"])\s*}/g, '$1"}')
      .replace(/"\s*"\s*}/g, '"}')
      .replace(/,\s*}/g, '}')
      .replace(/"citation":\s*[^"]([^"]+)/g, '"citation": "$1');

    try {
      answer = jsonrepair(answer);
    } catch (error) {
      console.error('Error parsing JSON:', error);
      console.log('Raw JSON for Dynamic KB job ' + jobId + ':\n' + answer);
      console.log('Processed JSON for Dynamic KB job ' + jobId + ':\n' + answer);
      throw new Error('JSONParsingError');
    }
  }
  const returnData = {
    type: data.type,
    answer,
    sec_id,
  };
  return returnData;
}

export const kbs = {
  createKbs,
  getKb,
};
